/**
 * Flat phosphor world map on a 2D canvas: screen backdrops and the lobby region picker.
 */
import type { LoadedWorld } from '@sa/mapdata';
import type { RegionId } from '@sa/sim';
import { COLORS, FONT_STACK, cssRgb, type RGB } from '../theme';

export interface RegionMark {
  id: RegionId;
  color: RGB;
  label?: string;
  /** dim label (unclaimed region), bright when hovered */
  dimLabel?: boolean;
  /** not in play this match (outside the lobby's region set): faint, unlabelled */
  off?: boolean;
}

/** Coastline rasters by size and style: hover redraws only the region marks (a 1:10m stroke is slow on software GL). */
const coastCache = new WeakMap<LoadedWorld, Map<string, HTMLCanvasElement>>();

/** The coastline at scale `s` (px per mu), simplified to ~1 px, as a canvas of w × h px. */
function coastLayer(world: LoadedWorld, w: number, h: number, s: number, ox: number, oy: number, a: number, dotted: boolean, dpr: number): HTMLCanvasElement {
  let byKey = coastCache.get(world);
  if (!byKey) coastCache.set(world, (byKey = new Map()));
  const key = `${w}x${h}|${a}|${dotted ? 1 : 0}`;
  let c = byKey.get(key);
  if (c) return c;
  if (byKey.size > 6) byKey.clear();
  c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d')!;
  ctx.strokeStyle = cssRgb(COLORS.me, a);
  ctx.lineWidth = Math.max(1, dpr * 0.8);
  if (dotted) ctx.setLineDash([1 * dpr, 2 * dpr]);
  ctx.beginPath();
  const minD2 = dpr * dpr;
  for (const ring of world.coast) {
    let lx = ox + ring[0] * s;
    let ly = oy + ring[1] * s;
    ctx.moveTo(lx, ly);
    const n = ring.length;
    for (let i = 2; i < n; i += 2) {
      const x = ox + ring[i] * s;
      const y = oy + ring[i + 1] * s;
      // drop points closer than a pixel to the last one drawn (always keep the ring's end)
      if ((x - lx) ** 2 + (y - ly) ** 2 < minD2 && i < n - 2) continue;
      ctx.lineTo(x, y);
      lx = x;
      ly = y;
    }
  }
  ctx.stroke();
  byKey.set(key, c);
  return c;
}

/** Draw the coastlines (and optional start regions) to fill `canvas`. Returns mu → px scale. */
export function drawWorld(
  canvas: HTMLCanvasElement,
  world: LoadedWorld,
  opts: { alpha?: number; marks?: RegionMark[]; hover?: RegionId | null; dotted?: boolean } = {},
): { s: number; ox: number; oy: number } {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const r = canvas.getBoundingClientRect();
  const w = Math.max(1, Math.round(r.width * dpr));
  const h = Math.max(1, Math.round(r.height * dpr));
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, w, h);
  const s = Math.min(w / world.width, h / world.height);
  const ox = (w - world.width * s) / 2;
  const oy = (h - world.height * s) / 2;
  const a = opts.alpha ?? 0.5;

  // start regions as hex dots
  for (const m of opts.marks ?? []) {
    const reg = world.regions.find((x) => x.id === m.id);
    if (!reg) continue;
    const hot = opts.hover === m.id && !m.off;
    ctx.fillStyle = cssRgb(m.color, m.off ? 0.12 : hot ? 0.9 : 0.55);
    const d = Math.max(1.5, s * 0.9);
    for (const hx of reg.coreHexes) ctx.fillRect(ox + world.cx[hx] * s - d / 2, oy + world.cy[hx] * s - d / 2, d, d);
    if (m.label && !m.off) {
      const dim = m.dimLabel && !hot;
      ctx.font = `${dim ? 400 : 600} ${Math.round(11 * dpr)}px ${FONT_STACK}`;
      ctx.fillStyle = cssRgb(dim ? COLORS.neutral : m.color, dim ? 0.55 : 0.95);
      ctx.shadowColor = cssRgb(m.color, 0.7);
      ctx.shadowBlur = dim ? 0 : 8;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      // above the hex cluster, never on it
      let top = Infinity;
      for (const hx of reg.coreHexes) top = Math.min(top, world.cy[hx]);
      ctx.fillText(m.label, ox + reg.anchor.x * s, oy + top * s - 8 * dpr - d / 2);
      ctx.shadowBlur = 0;
      ctx.textBaseline = 'alphabetic';
    }
  }

  ctx.drawImage(coastLayer(world, w, h, s, ox, oy, a, !!opts.dotted, dpr), 0, 0);
  return { s: s / dpr, ox: ox / dpr, oy: oy / dpr };
}

/** Region whose core contains (or is nearest to) a click, within a tolerance. */
export function regionAt(world: LoadedWorld, x: number, y: number, maxDist = 12): RegionId | null {
  let best: RegionId | null = null;
  let bd = maxDist * maxDist;
  for (const reg of world.regions) {
    for (const hx of reg.coreHexes) {
      const d = (world.cx[hx] - x) ** 2 + (world.cy[hx] - y) ** 2;
      if (d < bd) {
        bd = d;
        best = reg.id;
      }
    }
  }
  return best;
}
